import {
    componentClasses as calendarClasses,
    isValidDate,
} from "../../../core/components/date-picker/calendar";
import {
    bindMask,
    componentClasses,
    componentName,
    defaultProps,
    eventMap,
    schema,
    views,
} from "../../../core/components/date-picker/date-picker";
import { prefix } from "../../../core/design-tokens/meta";
import { buildFocusTrap } from "../../../core/js/focus-trap/focus-trap";
import { alertNotice, calendar as calendarIcon } from "../../../core/js/helpers/built-in-icons";
import validateInitialization from "../../../core/js/helpers/validate-initialization";
import validateRequiredElements from "../../../core/js/helpers/validate-required-elements";
import { getPosition } from "../../../core/js/positioning";
import {
    actions,
    createStore,
    dispatchEvent,
    reducers,
    renderState,
} from "../../../core/js/state-machine";
import { createCSSClass, createElement } from "../../../core/js/utilities";
import {
    buildMaskBasedOnDateFormat,
    formatDate,
    getDateFromString,
    isDateAfter,
    isDateBefore,
    isSameDateOrAfter,
    isSameDateOrBefore,
} from "../../../core/js/utils/date";
import { matchers, validate as validateProps } from "../../../core/js/validate-props";
import Calendar from "./calendar";
import { calendarTemplate } from "./calendar.template";

const validateHTML = (element) => {
    const requiredElements = {
        wrapper: {
            query: `.${componentClasses.wrapper}`,
            selector: element.querySelector(`.${componentClasses.wrapper}`),
        },
        inputField: {
            query: `.${componentClasses.input}`,
            selector: element.querySelector(`.${componentClasses.input}`),
        },
        invalidFeedback: {
            query: `.${componentClasses.invalidFeedback}[id]`,
            selector: element.querySelector(`.${componentClasses.invalidFeedback}[id]`),
        },
    };

    const customReport = {
        invalidFeedback: `${componentName} error validating required element. Missing element with class .${componentClasses.invalidFeedback} and an id`,
    };

    const [success, elements] = validateRequiredElements({
        requiredElements,
        componentName,
        customReport,
    });

    return success ? elements : false;
};

/**
 * Creates DatePicker
 * @param {HTMLElement} node
 * @returns {DatePicker|Void}
 */
const DatePicker = (element, props) => {
    const [success, mergedProps, validElements] = validateInitialization({
        componentName,
        element,
        defaultProps,
        props,
        views,
        schema,
        validateHTML,
    });

    if (!success) return;

    if (isSameDateOrAfter(mergedProps.startDate, mergedProps.endDate, "day")) {
        console.warn("startDate is same or after endDate");
        return;
    }

    /**
     * Inject validation icon
     */
    const injectValidationIcon = () => {
        inputField.insertAdjacentHTML(
            "afterend",
            alertNotice({ class: componentClasses.validationIcon })
        );
    };

    /**
     * Build calendar button
     * @returns {HTMLElement}
     */
    const buildCalendarButton = () => {
        const calendarButtonNode = createElement("button", {
            class: componentClasses.calendarButton,
            type: "button",
            "aria-label": mergedProps.calendarButtonSrLabel,
        });

        calendarButtonNode.insertAdjacentHTML("beforeend", calendarIcon());
        wrapper.appendChild(calendarButtonNode);

        return calendarButtonNode;
    };

    /**
     * Build the popup and the calendar component
     * @returns {Object} - Object containing popup element, calendar element and instance
     */
    const buildCalendar = () => {
        const popupNode = createElement("div", {
            class: `${componentClasses.popup.base} ${componentClasses.popup.hidden}`,
            tabindex: "-1",
            role: "dialog",
            "aria-modal": "true",
        });

        element.appendChild(popupNode);
        popupNode.insertAdjacentHTML("beforeend", calendarTemplate());
        const calendarNode = element.querySelector(`.${calendarClasses.calendar.base}`);

        const calendarInstance = Calendar(calendarNode, {
            ...mergedProps,
            onSelectDate: handleSelectDate,
            onCancelClick: handleCancelClick,
        });

        return {
            calendar: calendarNode,
            calendarInstance,
            popup: popupNode,
        };
    };

    /**
     * Get available position for the calendar popup and apply the dynamic class with the needed properties
     */
    const updatePopupPosition = () => {
        const { top, left } = getPosition(inputField, popup, "bottom-start", {
            x: 0,
            y: 2,
        });

        popupDynamicClass = `${prefix}dynamic-date-picker-t${parseInt(top)}-l${parseInt(left)}`;

        if (
            createCSSClass(`.${popupDynamicClass}`, {
                transform: `translate(${parseInt(left)}px, ${parseInt(top)}px) !important`,
            })
        ) {
            renderState(
                {
                    classList: [
                        popupDynamicClass,
                        popupDynamicClassMemory !== popupDynamicClass &&
                            `!${popupDynamicClassMemory}`,
                    ],
                },
                popup
            );
        }

        // Save last used class
        popupDynamicClassMemory = popupDynamicClass;
    };

    /**
     * Check the value inserted in the field is a valid date and if the date is valid against date picker configurations
     * @param {Event} e
     */
    const handleInputBlur = (e) => {
        const date = getDateFromString(e.target.value, mergedProps.format);
        const valid = validate(date);

        // Because blur can change the date (clearing it if the date is incomplete)
        // We will flag preventOnSelect as true, so using calendarInstance don't generate a loop when using handleSelectDate
        preventOnSelect = true;

        if (!date || !valid) {
            calendarInstance.clearSelectedDate();
        } else {
            setFieldDate(date);
            calendarInstance.selectDate(date);
        }
    };

    /**
     * Remove invalid state from the component
     */
    const unsetInvalidState = () => {
        inputField.removeAttribute("aria-describedby");
        renderState(views.datePicker.valid, element);
    };

    /**
     * Set invalid state on component based on a Date
     * @param {Date} date
     */
    const setInvalidState = (date) => {
        const invalidFeedbackId = invalidFeedback.getAttribute("id");
        inputField.setAttribute("aria-describedby", invalidFeedbackId);

        // Check before for an invalid date first so we can differentiate errors
        if (!matchers.date(date)) {
            invalidFeedback.textContent = mergedProps.invalidDateErrorMessage;
        } else {
            invalidFeedback.textContent = mergedProps.unavailableDateErrorMessage;
        }

        renderState(views.datePicker.invalid, element);
    };

    /**
     * Validate a date updating the sate of the component
     * @param {Date} date
     * @returns {Boolean|Void}
     */
    const validate = (date) => {
        const { startDate, endDate, inactiveDates } = mergedProps;

        if (isValidDate(date, startDate, endDate, inactiveDates)) {
            unsetInvalidState();
            return true;
        }

        if (dirty) {
            setInvalidState(date);
            return false;
        }
    };

    /**
     * Handle input change
     * @param {Event} e
     */
    const handleInputChange = (e) => {
        if (!dirty) dirty = true;
        bindMask(e, buildMaskBasedOnDateFormat(mergedProps.format), inputField);
    };

    /**
     * Handle input focus
     * @param {FocusEvent} e
     */
    const handleInputFocus = (e) => {
        if (e.target.value && !dirty) dirty = true;
    };

    /**
     * Handle calendar button click
     */
    const handleCalendarButtonClick = () => {
        if (visibilityStore.getState()) {
            visibilityStore.dispatch(actions.close());
        } else {
            visibilityStore.dispatch(actions.open());
        }
    };

    /**
     * Handle calendar select date callback
     * @param {*} date
     * @returns {undefined}
     */
    const handleSelectDate = (date) => {
        if (preventOnSelect) {
            preventOnSelect = false;
            return;
        }

        setFieldDate(date);
        inputField.focus();
        visibilityStore.dispatch(actions.close());

        inputField.removeAttribute("aria-describedby");
        renderState(views.datePicker.valid, element);

        dispatchEvent(eventMap.selectDate, element, {
            formattedDate: date ? formatDate(date, mergedProps.format) : null,
            date,
        });
    };

    /**
     * Handle calendar cancel callback
     */
    const handleCancelClick = () => {
        visibilityStore.dispatch(actions.close());
    };

    /**
     * Handle popup blur closing it if needed
     * @param {Event} e
     */
    const handlePopupBlur = (e) => {
        if (
            visibilityStore.getState() &&
            !popup.contains(e.target) &&
            !calendarButton.contains(e.target)
        ) {
            visibilityStore.dispatch(actions.close());
        }
    };

    /**
     * Handle poopup event closing if needed
     * @param {Event} e
     */
    const handlePopupKeydown = (e) => {
        if (e.keyCode === 27) {
            e.preventDefault();
            visibilityStore.dispatch(actions.close());
            calendarButton.focus();
        }
    };

    /**
     * Handle popup visibility
     * @param {Boolean} visible
     */
    const handleVisibleStateChange = (visible) => {
        if (visible) {
            renderState(views.popup.positioning, popup);
            updatePopupPosition();
            renderState(views.popup.visible, popup);
            const focusedSlot = calendarInstance.getFocusedSlot();
            focusedSlot && focusedSlot.focus();
        } else {
            renderState(views.popup.hidden, popup);
        }
    };

    /**
     * Set formatted value on input based on a date
     * @param {Date} date
     */
    const setFieldDate = (date) => {
        inputField.value = formatDate(date, mergedProps.format);
    };

    /**
     * Clear input value
     */
    const clearFieldDate = () => {
        inputField.value = "";
    };

    /**
     * Set a date on field based on mergedProps.selectedDate
     */
    const setSelectedDate = () => {
        if (mergedProps.selectedDate) {
            setFieldDate(mergedProps.selectedDate);
        } else {
            clearFieldDate();
        }
    };

    /**
     * Set date on component
     * @param {Date} date
     */
    const setDate = (date) => {
        if (
            isValidDate(date, mergedProps.startDate, mergedProps.endDate, mergedProps.inactiveDates)
        ) {
            setFieldDate(date);
            calendarInstance.selectDate(date);
        } else {
            console.warn(`${componentName} invalid date provided`);
        }
    };

    /**
     * Get date from the component
     * @returns {Date|null}
     */
    const getDate = () => {
        const date = getDateFromString(inputField.value, mergedProps.format);
        return { date, formattedDate: date ? formatDate(date, mergedProps.format) : null };
    };

    /**
     * Set the start date property
     * @param {Date} startDate
     * @returns {unddefined}
     */
    const setStartDate = (startDate) => {
        const [validateSuccess] = validateProps({ startDate }, { startDate: schema.startDate });

        const selectedDate = getDateFromString(inputField.value, mergedProps.format);

        if (!validateSuccess) {
            console.warn(`${componentName} startDate is invalid`);
            return;
        }

        if (!isDateBefore(startDate, mergedProps.endDate)) {
            console.warn(`${componentName} startDate must be a date before as endDate`);
            return;
        }

        if (selectedDate && !isSameDateOrBefore(startDate, selectedDate)) {
            console.warn(`${componentName} startDate must be a date same or before as selectDate`);
            return;
        }

        mergedProps.startDate = startDate;
        calendarInstance.setStartDate(startDate);
    };

    /**
     * Set the end date property
     * @param {Date} endDate
     * @returns {unddefined}
     */
    const setEndDate = (endDate) => {
        const [validateSuccess] = validateProps({ endDate }, { endDate: schema.endDate });

        const selectedDate = getDateFromString(inputField.value, mergedProps.format);

        if (!validateSuccess) {
            console.warn(`${componentName} endDate is invalid`);
            return false;
        }

        if (!isDateAfter(endDate, mergedProps.startDate)) {
            console.warn(`${componentName} endDate must be a date after as startDate`);
            return false;
        }

        if (selectedDate && !isSameDateOrAfter(endDate, selectedDate)) {
            console.warn(`${componentName} endDate must be a date same or after as selectDate`);
            return false;
        }

        mergedProps.endDate = endDate;
        calendarInstance.setEndDate(endDate);
    };

    /**
     * Dispose events and reset element inner variables
     */
    const dispose = () => {
        inputField.removeEventListener("blur", handleInputBlur);
        inputField.removeEventListener("input", handleInputChange);
        inputField.removeEventListener("focus", handleInputFocus);
        calendarButton.removeEventListener("click", handleCalendarButtonClick);
        popup.removeEventListener("keydown", handlePopupKeydown);
        window.removeEventListener("click", handlePopupBlur, true);
        disposeFocusTrap();

        element[componentName] = null;
        element = null;
    };

    const { wrapper, inputField, invalidFeedback } = validElements;
    const calendarButton = buildCalendarButton();
    const { calendar, calendarInstance, popup } = buildCalendar();
    const { dispose: disposeFocusTrap } = buildFocusTrap(calendar);

    injectValidationIcon();

    let preventOnSelect = false;
    let dirty = false;
    let popupDynamicClass = null;
    let popupDynamicClassMemory = null;

    setSelectedDate();

    const visibilityStore = createStore(reducers.boolReducer, false);
    visibilityStore.subscribe(handleVisibleStateChange);

    inputField.addEventListener("blur", handleInputBlur);
    inputField.addEventListener("input", handleInputChange);
    inputField.addEventListener("focus", handleInputFocus);
    calendarButton.addEventListener("click", handleCalendarButtonClick);
    popup.addEventListener("keydown", handlePopupKeydown);
    window.addEventListener("click", handlePopupBlur, true);

    const api = {
        element,
        dispose,
        getDate,
        setDate,
        setStartDate,
        setEndDate,
    };

    element[componentName] = api;

    return api;
};

export default DatePicker;
